import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, computed, effect, input, signal, viewChild } from '@angular/core';
import { DealsService } from '../../../services';
import { Router, RouterModule } from '@angular/router';
import { Deal, RequestDeal, RequestDealSet, ResponseDeal, ResponseDealSet } from '../../../models';
import { finalize, map, takeUntil } from "rxjs/operators";
import { SortDescriptor } from '@progress/kendo-data-query';
import { State } from "@progress/kendo-data-query";
import { CommonModule } from '@angular/common';
import { ConvertToMillionPipe, NumericFormatPipe, SharedWithUserPipe } from '../../../pipes';
import { ConfirmationDialogInputService, NotificationDialogService } from '../../../../../modules/fwcore';
import { Subject } from 'rxjs';
import { PbCreatenewDealDlgComponent } from '../pb-createnew-deal-dlg/pb-createnew-deal-dlg.component';
import { ImpersonationService } from '../../../../../modules/fwcore/services/impersonation.service';
import { HelpTooltipService } from '../../../services/help-tooltip.service';
import { EnumDealGroupId } from '../../../models/constants/enumDealGroupId';

@Component({
  selector: 'app-pb-recent-items',
  standalone: true,
  imports: [CommonModule, RouterModule
    , NumericFormatPipe
    , SharedWithUserPipe, ConvertToMillionPipe
    , PbCreatenewDealDlgComponent],
  templateUrl: './pb-recent-items.component.html',
  styleUrl: './pb-recent-items.component.scss'
})
export class PbRecentItemsComponent implements OnInit, AfterViewInit, OnDestroy {


  //------------------------- Constants ----------------------------------

  /** Sort direction descending literal */
  protected readonly ctDesc = 'desc';

  /** Field name for last modified date sorting */
  protected readonly ctLastModifiedOn = 'lastModifiedOn';

  /** Fallback accent when the deal group is unknown */
  protected readonly ctDefaultAccent = '#2e6fb8';

  /** Accent colour per deal group */
  protected readonly accentByGroup: Record<EnumDealGroupId, string> = {
    [EnumDealGroupId.ItServices]: '#2e6fb8',
    [EnumDealGroupId.Telecom]: '#1c8a8a',
    [EnumDealGroupId.ItLaborRates]: '#c98a1a',
    [EnumDealGroupId.Fao]: '#7a4fc0',
    [EnumDealGroupId.Procurement]: '#2a8fb0',
    [EnumDealGroupId.CCaaS]: '#c2557b'
  };

  /** Company filter applied to the recent-items query */
  public filterCompanyId = input<string | null>(null);


  /** Grid metrics read back from resolved layout */
  protected columns = signal<number>(1);
  protected rowHeight = signal<number>(0);
  protected rowGap = signal<number>(0);

  /** True once the grid has reported a usable box */
  protected isMeasured = signal<boolean>(false);

  /** The tile grid container */
  protected grid = viewChild<ElementRef<HTMLElement>>('gridRecentItems');

  /** Measured inner size of the grid (px) */
  protected gridWidth = signal<number>(0);
  protected gridHeight = signal<number>(0);



  /** Whole rows that fit the measured height */
  protected rows = computed(() => {

    const height = this.gridHeight();
    const rowH = this.rowHeight();
    const gap = this.rowGap();

    if (height <= 0 || rowH <= 0) {

      return 1;
    }

    const fit = Math.floor((height + gap) / (rowH + gap));

    return Math.max(1, fit);
  });

  /** Deals sliced to fill the cells left after the create-new tile */
  protected visibleItems = computed(() => {

    const items = this.recentItems();

    if (!this.isMeasured()) {

      return items;
    }

    const cells = this.columns() * this.rows();
    const dealSlots = Math.max(0, cells - 1);

    return items.slice(0, dealSlots);
  });

  //--------------------- Component State --------------------------------

  /** Subject for component destruction */
  protected destroy$ = new Subject();

  /** Flag to open the create new deal dialog */
  protected showCreateNewDealDlg = signal<boolean>(false);

  //--------------------- Data Collections -------------------------------

  /** Recent deal items returned from the data service */
  protected recentItems = signal<Deal[]>([]);

  //-------------------- Implementation State ----------------------------

  /** Request object for deal queries */
  protected req: RequestDeal = {
    skip: 0
    , take: 60
  };

  /** Grid state configuration */
  protected state: State = {
    skip: 0
    , take: 15
    , sort: [{
      field: this.ctLastModifiedOn
      , dir: this.ctDesc
    }]
  }

  //---------------------------- Constructor ------------------------------

  constructor(
    protected router: Router
    , protected dataService: DealsService
    , protected dialogConfirmInput: ConfirmationDialogInputService
    , protected dialogNotification: NotificationDialogService
    , protected impersonationService: ImpersonationService
    , protected helpTooltip: HelpTooltipService
  ) {

    this.setupDebounceEffectHandlers();
  }

  //------------------------ Lifecycle Methods ----------------------------

  ngOnInit(): void {

  }

  ngAfterViewInit(): void {

    requestAnimationFrame(() => this.measureGrid());
  }

  /** Cleans up subscriptions when the component is destroyed */
  ngOnDestroy(): void {

    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Registers debounce subscriptions and reactive effects for the component */
  protected setupDebounceEffectHandlers(): void {

    effect(() => {
      this.impersonationService.impersonating();
      this.filterCompanyId();
      this.getData();
    }, { allowSignalWrites: true });

    this.setupGridObserver();
  }

  /** Counts the resolved column tracks the browser laid out */
  protected readColumnCount(style: CSSStyleDeclaration): number {

    const template = style.gridTemplateColumns;

    if (!template || template === 'none') {

      return 1;
    }

    return template.split(' ').length;
  }

  /** Reads the grid's resolved metrics into the measurement signals */
  protected measureGrid(): void {

    const el = this.grid()?.nativeElement;

    if (el == null) {

      return;
    }

    const height = el.clientHeight;
    const style = getComputedStyle(el);
    const rowH = parseFloat(style.gridAutoRows) || 0;

    if (height <= 0 || rowH <= 0) {

      return;
    }

    this.gridHeight.set(height);
    this.columns.set(this.readColumnCount(style));
    this.rowHeight.set(rowH);
    this.rowGap.set(parseFloat(style.rowGap) || 0);
    this.isMeasured.set(true);
  }

  /** Tracks the grid's resolved metrics so the clip recomputes on resize */
  protected setupGridObserver(): void {

    effect((onCleanup) => {

      const el = this.grid()?.nativeElement;

      if (el == null) {

        return;
      }

      const observer = new ResizeObserver(() => this.measureGrid());

      observer.observe(el);
      onCleanup(() => observer.disconnect());
    });
  }

  //--------------------- Event Handlers ---------------------------------

  /** Handles view deal click navigation */
  protected onViewDealClick(dataItem: Deal): void {

    this.router.navigate(
      ['/scenario']
      , { queryParams: { id: dataItem.id } }
    );
  }

  /** Handles add deal button click and opens the create new deal dialog */
  protected onAddDealClick(): void {

    this.showCreateNewDealDlg.set(false);

    setTimeout(() => {
      this.showCreateNewDealDlg.set(true);
    });
  }

  //------------------- UI / Tile Helpers --------------------------------

  /** Returns the background color for a tile based on its index */
  protected getBackgroundColor(index: number): string {

    if (index === 4) {
      return '#76b2dc';
    }
    if (index === 3) {
      return '#b9a5de';
    }
    if (index === 2) {
      return '#ffc72c';
    }
    if (index === 1) {
      return '#cbd9ee';
    }
    if (index === 0) {
      return '#d1c3e9';
    }
    if (index === 5) {
      return '#c2e089';
    }
    return 'transparent';
  }

  /** Returns the accent colour for a tile based on its index */
  protected getAccentColourOld(index: number): string {

    const accents = ['#2e6fb8', '#1c8a8a', '#c98a1a', '#7a4fc0', '#2a8fb0'];

    return accents[index % accents.length];
  }

  /** Returns the accent colour for a tile based on its deal group */
  protected getAccentColour(deal: Deal): string {

    const groupId = EnumDealGroupId.fromString(deal.dealGroupId);

    if (groupId === null) {

      return this.ctDefaultAccent;
    }

    return this.accentByGroup[groupId];
  }

  /** Returns the meta label for a tile: draft state or last-edited time */
  protected metaLabel(deal: Deal): string {

    if (deal.targetPrice == null) {

      return 'Draft';
    }

    const edited = this.relativeEdited(deal.lastModifiedOn);

    if (edited === '') {

      return 'Edited recently';
    }

    return `Edited ${edited}`;
  }

  /** Returns a short relative-time phrase for a past date */
  protected relativeEdited(date: Date | string | null | undefined): string {

    if (date == null) {

      return '';
    }

    const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000);

    if (seconds < 60) {

      return 'just now';
    }

    const minutes = Math.floor(seconds / 60);

    if (minutes < 60) {

      return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    }

    const hours = Math.floor(minutes / 60);

    if (hours < 24) {

      return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    }

    const days = Math.floor(hours / 24);

    if (days === 1) {

      return 'yesterday';
    }

    if (days < 30) {

      return `${days} days ago`;
    }

    const months = Math.floor(days / 30);

    if (months < 12) {

      return `${months} month${months === 1 ? '' : 's'} ago`;
    }

    const years = Math.floor(months / 12);

    return `${years} year${years === 1 ? '' : 's'} ago`;
  }

  /** Joins tower names into a newline-separated tooltip string */
  protected towerTooltip(deal: Deal): string {

    return deal.towerNames?.join('\n') ?? '';
  }

  //---------------------- Data Transformation ----------------------------

  /** Converts Kendo sort descriptors into a JSON string for the API */
  protected convertSortDescriptorsToJsonString(sortDescriptors: SortDescriptor[] | undefined | null): string | null {

    if (sortDescriptors === null
      || sortDescriptors === undefined
      || sortDescriptors.length < 1) {

      return null;
    }

    const sortJson: Record<string, 'asc' | 'desc'> = {};

    sortDescriptors.forEach(item => {

      if (item.dir) {
        sortJson[item.field] = item.dir;
      }
    });

    return JSON.stringify(sortJson);
  }

  //-------------------------- API Calls ---------------------------------

  /** Fetches deal grid data based on current filters */
  protected getData(): void {

    const filterCompany = this.filterCompanyId();

    this.req.dealGroupId = null;
    this.req.userCompanyId = filterCompany;

    this.req.userGroupCode = null;
    this.req.dealFolderId = null;
    this.req.onlyReceived = null;
    this.req.sharedWith = null;
    this.req.sort = this.convertSortDescriptorsToJsonString(this.state.sort);

    this.dataService.getData$<RequestDeal, ResponseDeal>(this.req, this.dataService.endPointDeals)
      .subscribe(
        (resp) => {
          this.recentItems.set(resp.data ?? []);
          requestAnimationFrame(() => this.measureGrid());
        }
      );
  }

}
